import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'

/**
 * A surface container for grouping related content.
 *
 * Flat by design: the design system reserves shadows for menus and dialogs, so
 * a card is separated from the page by its 1px border and the app background
 * behind it, not by elevation.
 */
export const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function Card({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border border-default bg-surface text-primary-text',
        className,
      )}
      {...props}
    />
  )
})

/** The header region of a `Card`. */
export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CardHeader({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1 p-4', className)}
      {...props}
    />
  )
})

interface CardTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Render the title's styles onto its single child instead of an `<h3>`. Use
   * this when the card is the main content of a page and its title should be
   * the page's `<h1>` — styling must not dictate heading level.
   */
  asChild?: boolean
}

/** The title line of a `Card` header. */
export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  function CardTitle({ className, asChild = false, ...props }, ref) {
    const Comp = asChild ? Slot : 'h3'
    return (
      <Comp
        ref={ref}
        className={cn('text-lg font-semibold leading-tight', className)}
        {...props}
      />
    )
  },
)

/** Secondary descriptive text within a `Card` header. */
export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-muted-text', className)}
      {...props}
    />
  )
})

/** The main body region of a `Card`. */
export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CardContent({ className, ...props }, ref) {
  return <div ref={ref} className={cn('p-4 pt-0', className)} {...props} />
})
